/**
 * AI API
 * Contains all API functions related to AI/LLM interactions and document processing
 */

import { apiGet, apiPost, apiPut, apiDelete } from './apiClient';

export interface LLMQueryParams {
    query: string;
    context?: string;
    documentId?: string;
    userId: string;
}

export interface UpdateLLMQueryParams {
    id: string;
    query: string;
    response: string;
    rating?: number;
    feedback?: string;
}

export interface PersistPDFParams {
    content: string;
    metadata: {
        filename: string;
        size: number;
        pages: number;
        userId: string;
    };
}

export interface CreateSubscriptionParams {
    userId: string;
    planId: string;
    paymentMethodId: string;
}

export interface CreateStripeAccountParams {
    userId: string;
    email: string;
    country: string;
}

/**
 * Spin up Llama server
 * @returns Promise with server status
 */
export const spinUpLlamaServer = async () => {
    return apiGet('/ai/llama/spinup', {}, '');
};

/**
 * Get LLM chat history
 * @param userId The user ID
 * @returns Promise with chat history
 */
export const getLLMChats = async (userId: string) => {
    return apiGet(`/ai/chats/${userId}`, {}, 'authenticated');
};

/**
 * Update LLM query
 * @param params Update parameters
 * @returns Promise with update result
 */
export const updateLLMQuery = async (params: UpdateLLMQueryParams) => {
    return apiPut('/ai/query/update', params, {}, 'authenticated');
};

/**
 * Delete LLM query
 * @param id The query ID
 * @returns Promise with delete result
 */
export const deleteLLMQuery = async (id: string) => {
    return apiDelete(`/ai/query/${id}`, {}, 'authenticated');
};

/**
 * Persist PDF content
 * @param params PDF content and metadata
 * @returns Promise with persistence result
 */
export const persistPDFContent = async (params: PersistPDFParams) => {
    return apiPost('/ai/pdf/persist', params, {}, 'authenticated');
};

/**
 * Query LLM
 * @param params Query parameters
 * @returns Promise with AI response
 */
export const queryLLM = async (params: LLMQueryParams) => {
    return apiPost('/llm', params, { timeout: 60000 }, '', false, 'ai');
};

/**
 * Find Funding
 * @param params Query parameters
 * @returns Promise with AI response
 */
export const findFunding = async (params: LLMQueryParams) => {
    return apiPost('/funding', params, { timeout: 60000 }, '', false, 'ai');
};

/**
 * Create subscription
 * @param params Subscription parameters
 * @returns Promise with subscription result
 */
export const createSubscription = async (params: CreateSubscriptionParams) => {
    return apiPost('/ai/subscription/create', params, {}, 'authenticated');
};

/**
 * Create Stripe account
 * @param params Stripe account parameters
 * @returns Promise with account creation result
 */
export const createStripeAccount = async (params: CreateStripeAccountParams) => {
    return apiPost('/ai/stripe/account', params, {}, 'authenticated');
};

/**
 * Health check for AI services
 * @returns Promise with health status
 */
export const healthCheck = async () => {
    return apiGet('/ai/health', {}, '');
};

/**
 * Save data to local storage
 * @param key Storage key
 * @param value Data to store
 */
export const saveLocalData = async (key: string, value: any) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        console.error('Failed to save local data:', error);
    }
};

/**
 * Get data from local storage
 * @param key Storage key
 * @returns Stored data or null
 */
export const getLocalData = async (key: string) => {
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
    } catch (error) {
        console.error('Failed to get local data:', error);
        return null;
    }
};

/**
 * Default route for AI services
 */
export const defaultRoute = '/ai';
